import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAppBus } from '@/services/AppBus';
import { type IClock } from '@/services/Clock';
import { type IFileOutput } from '@/services/FileOutput';
import {
  epochDayIso,
  type PurchaseRecommendation,
  type SimulationResult,
} from '@/domain/beforered';
import { type LocaleStore } from './LocaleStore';
import { type SimulationStore } from './SimulationStore';

const csvEscape = (s: string): string => {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

// CSV column names match the canonical PurchaseRecommendation field names so the file
// round-trips into ERP intake scripts and into beforered's own importers without a
// rename step. Locale-specific labels live in the UI table; the file format is stable.
const FILE_HEADER = [
  'orderByDate',
  'sku',
  'description',
  'currentOnHand',
  'suggestedQuantity',
  'neededByDate',
  'uom',
  'drivingOrders',
  'asOf',
];

export class ResultsStore {
  constructor(
    private bus: IAppBus,
    private clock: IClock,
    private fileOutput: IFileOutput,
    private simulation: SimulationStore,
    private locale: LocaleStore,
  ) {
    makeAutoObservable(this);
    bus.subscribe('SIMULATION_COMPLETED', this.onSimulationCompleted);
  }

  private onSimulationCompleted = (data: {
    recommendationCount: number;
    durationMs: number;
  }): void => {
    const t = this.locale.messages.toasts;
    const n = data.recommendationCount;
    const ms = Math.round(data.durationMs);
    const body = n === 0 ? t.simNoPurchases : `${n} ${t.simRecommendationCount}`;
    this.bus.toast('success', `${body} · ${ms}ms`);
  };

  get result(): SimulationResult | null {
    return this.simulation.result;
  }

  get recommendations(): readonly PurchaseRecommendation[] {
    return this.simulation.result?.recommendations ?? [];
  }

  get hasResult(): boolean {
    return this.simulation.hasResult;
  }

  download = async (): Promise<void> => {
    const result = this.simulation.result;
    if (!result) return;
    const ranAtIso = epochDayIso(result.ranAt);
    const rows = result.recommendations.map((r) => [
      epochDayIso(r.orderByDate),
      r.sku,
      r.description,
      r.currentOnHand.toString(),
      r.suggestedQuantity.toFixed(2),
      epochDayIso(r.neededByDate),
      r.uom,
      r.drivingOrders.join('; '),
      ranAtIso,
    ]);
    const csv = [FILE_HEADER, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n') + '\n';
    const ts = new Date(this.clock.now()).toISOString().replace(/[:T]/g, '-').slice(0, 19);
    const filename = `purchase_recommendations_${ts}.csv`;
    this.fileOutput.download(filename, csv);
    await this.bus.exportTriggered({ filename, rowCount: result.recommendations.length });
  };
}

export const TOKEN_ResultsStore = token<ResultsStore>('ResultsStore');
